import React, { useEffect, useRef, useState } from "react";
import { saveLinks, loadUsers, saveUsers, saveAnnouncements, saveSettings, uploadImage, loadAccessLog, loadAuditLog, logAudit, resolveCategories } from "../services/storageService";
import { useAuth } from "../contexts/AuthContext";
import TabIcon, { TAB_ICON_OPTIONS, resolveTabIcon } from "../components/TabIcon";

// Cada cor tem um significado semantico fixo (ver Manual e mockup-tags.html).
const COLOR_OPTIONS = [
  { value: "gray",   label: "Cinza — manuais e procedimentos",  bg: "#F1EFE8", fg: "#5F5E5A" },
  { value: "teal",   label: "Verde — métricas e análise",       bg: "#E1F5EE", fg: "#0F6E56" },
  { value: "amber",  label: "Âmbar — formulários e afazeres",   bg: "#FAEEDA", fg: "#854F0B" },
  { value: "purple", label: "Roxo — cursos e treinamentos",     bg: "#EEEDFE", fg: "#534AB7" },
  { value: "blue",   label: "Azul — privacidade e segurança",   bg: "#E6F1FB", fg: "#185FA5" },
  { value: "coral",  label: "Coral — assuntos gerais",          bg: "#FAECE7", fg: "#993C1D" },
];

const EMPTY_LINK = { title: "", desc: "", url: "", color: "gray", tab: "", access: "public", subtab: "" };

const EMPTY_TAB  = { label: "", access: "public", icon: "" };
const EMPTY_USER = { email: "", role: "usuario" };
const EMPTY_ANNOUNCEMENT = { title: "", text: "", imageUrl: "", linkUrl: "", createdAt: "", expiresAt: "", priority: 0, subtab: "", pinned: false };
const PINNED_LIMIT = 5;

const ROLE_OPTIONS = [
  { value: "admin",    label: "Administrador" },
  { value: "lider",    label: "Líder" },
  { value: "usuario",  label: "Usuário" },
];

export default function AdminPage({ linksData, onSaved, announcementsData, onAnnouncementsSaved, settingsData, onSettingsSaved }) {
  const { account } = useAuth();
  const currentUser = account?.username || account?.name || "desconhecido";
  const [data, setData]           = useState(linksData);
  const [settings, setSettings]   = useState(settingsData || { logoUrl: "" });
  const [newCategory, setNewCategory] = useState("");
  const [view, setView]           = useState("announcements");

  // Categorias (sub-abas) atuais — vêm de settings, com fallback no padrão.
  const categories = resolveCategories(settings);
  const [pendingAudit, setPendingAudit] = useState([]);

  // Adiciona uma entrada à fila de auditoria (só é persistida ao Salvar)
  function queueAudit(action, target, details) {
    setPendingAudit(prev => [...prev, {
      timestamp: new Date().toISOString(),
      user: currentUser,
      action, target, details,
    }]);
  }
  const [linkModal, setLinkModal] = useState(null);
  const [tabModal,  setTabModal]  = useState(null);
  const [userModal, setUserModal] = useState(null);
  const [announcementModal, setAnnouncementModal] = useState(null);
  const [linkForm,  setLinkForm]  = useState(EMPTY_LINK);
  const [tabForm,   setTabForm]   = useState(EMPTY_TAB);
  const [userForm,  setUserForm]  = useState(EMPTY_USER);
  const [announcementForm, setAnnouncementForm] = useState(EMPTY_ANNOUNCEMENT);
  const [users,     setUsers]     = useState([]);
  const [announcements, setAnnouncements] = useState(announcementsData?.announcements || []);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);

  useEffect(() => {
    loadUsers().then(d => setUsers(d.users || []));
  }, []);

  const allSections = [
    ...data.public.map(s => ({ ...s, access: "public" })),
    ...data.restricted.map(s => ({ ...s, access: "restricted" })),
  ];

  async function handleSave() {
    setSaving(true);
    if (view === "users") {
      await saveUsers({ users });
    } else if (view === "announcements") {
      await saveAnnouncements({ announcements });
      onAnnouncementsSaved({ announcements });
    } else if (view === "settings") {
      await saveSettings(settings);
      onSettingsSaved(settings);
    } else {
      await saveLinks(data);
      onSaved(data);
    }
    // Flush da fila de auditoria — só persiste o que foi efetivamente salvo
    if (pendingAudit.length > 0) {
      const relevant = pendingAudit.filter(e => matchesView(e.target, view));
      if (relevant.length > 0) {
        await logAudit(relevant).catch(() => {});
      }
      setPendingAudit(prev => prev.filter(e => !matchesView(e.target, view)));
    }
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function openAddAnnouncement() {
    const maxPriority = announcements.reduce((m, a) => Math.max(m, a.priority || 0), 0);
    setAnnouncementForm({
      ...EMPTY_ANNOUNCEMENT,
      createdAt: toLocalInput(new Date()),
      expiresAt: "", // em branco — o admin precisa escolher a data
      priority: maxPriority + 1,
    });
    setAnnouncementModal({ mode: "add" });
  }

  function openEditAnnouncement(a) {
    // Compatibilidade com o formato antigo (type + content)
    const legacyText  = a.type === "text"  ? (a.content || "") : "";
    const legacyImage = a.type === "image" ? (a.content || "") : "";
    setAnnouncementForm({
      title: a.title || "",
      text: a.text != null ? a.text : legacyText,
      imageUrl: a.imageUrl != null ? a.imageUrl : legacyImage,
      linkUrl: a.linkUrl || "",
      createdAt: a.createdAt ? toLocalInput(new Date(a.createdAt)) : toLocalInput(new Date()),
      expiresAt: a.expiresAt ? toLocalInput(new Date(a.expiresAt)) : "",
      priority: a.priority || 0,
      subtab: a.subtab || "",
      pinned: !!a.pinned,
    });
    setAnnouncementModal({ mode: "edit", id: a.id });
  }

  // Conta quantos recados estao fixados, excluindo o que esta sendo editado.
  function countPinned(excludingId) {
    return announcements.filter(a => a.pinned && a.id !== excludingId).length;
  }

  function applyAnnouncementForm() {
    const hasText  = announcementForm.text.trim();
    const hasImage = announcementForm.imageUrl.trim();
    if (!hasText && !hasImage) return; // precisa de pelo menos um dos dois
    const createdAtIso = announcementForm.createdAt
      ? new Date(announcementForm.createdAt).toISOString()
      : new Date().toISOString();
    // Expiração é obrigatória. Recados fixos expiram automaticamente em 10 anos;
    // os demais exigem que o admin informe a data.
    let expiresAtIso;
    if (announcementForm.pinned) {
      expiresAtIso = plusYears(new Date(), 10).toISOString();
    } else {
      const parsed = announcementForm.expiresAt ? new Date(announcementForm.expiresAt) : null;
      if (!parsed || isNaN(parsed.getTime())) {
        window.alert("Data de expiração inválida ou não preenchida. Escolha uma data válida (ou marque \"Fixar\" para expirar em 10 anos).");
        return;
      }
      expiresAtIso = parsed.toISOString();
    }
    const priority = parseInt(announcementForm.priority, 10) || 0;
    const rawLink = (announcementForm.linkUrl || "").trim();
    const linkUrl = rawLink && !/^https?:\/\//i.test(rawLink) ? `https://${rawLink}` : rawLink;
    // Limite de 5 recados fixos
    const editingId = announcementModal.mode === "edit" ? announcementModal.id : null;
    if (announcementForm.pinned && countPinned(editingId) >= PINNED_LIMIT) {
      window.alert(`Limite de ${PINNED_LIMIT} recados fixos atingido. Desafixe outro antes.`);
      return;
    }
    const payload = {
      title: announcementForm.title,
      text: announcementForm.text,
      imageUrl: announcementForm.imageUrl,
      linkUrl,
      createdAt: createdAtIso,
      expiresAt: expiresAtIso,
      priority,
      subtab: announcementForm.subtab || "",
      pinned: !!announcementForm.pinned,
    };
    const next = [...announcements];
    const summary = payload.title || (payload.text ? payload.text.slice(0, 40) : "sem título");
    if (announcementModal.mode === "edit") {
      const idx = next.findIndex(a => a.id === announcementModal.id);
      if (idx !== -1) {
        // Limpa campos antigos para não ficarem duplicados no blob
        const { type, content, ...rest } = next[idx];
        next[idx] = { ...rest, ...payload };
        queueAudit("update", "announcement", `Recado "${summary}" editado`);
      }
    } else {
      next.push({ id: Date.now().toString(), ...payload });
      queueAudit("create", "announcement", `Recado "${summary}" adicionado`);
    }
    setAnnouncements(next);
    setAnnouncementModal(null);
  }

  function moveAnnouncement(a, delta) {
    // Reordena por prioridade, faz o swap e renumera 1..N
    const sorted = [...announcements].sort(comparePriority);
    const idx = sorted.findIndex(x => x.id === a.id);
    if (idx === -1) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    [sorted[idx], sorted[newIdx]] = [sorted[newIdx], sorted[idx]];
    const renumbered = sorted.map((x, i) => ({ ...x, priority: i + 1 }));
    setAnnouncements(renumbered);
  }

  function normalizePriorities() {
    const sorted = [...announcements].sort(comparePriority);
    const renumbered = sorted.map((x, i) => ({ ...x, priority: i + 1 }));
    setAnnouncements(renumbered);
  }

  function deleteAnnouncement(a) {
    if (!window.confirm("Remover este recado?")) return;
    const summary = a.title || (a.text ? a.text.slice(0, 40) : "sem título");
    queueAudit("delete", "announcement", `Recado "${summary}" removido`);
    setAnnouncements(announcements.filter(x => x.id !== a.id));
  }

  function togglePinned(a) {
    if (!a.pinned && countPinned(a.id) >= PINNED_LIMIT) {
      window.alert(`Limite de ${PINNED_LIMIT} recados fixos atingido. Desafixe outro antes.`);
      return;
    }
    const summary = a.title || (a.text ? a.text.slice(0, 40) : "sem título");
    const next = announcements.map(x => x.id === a.id ? { ...x, pinned: !x.pinned } : x);
    queueAudit("update", "announcement", `Recado "${summary}" ${a.pinned ? "desafixado" : "fixado"} na lateral`);
    setAnnouncements(next);
  }

  function openAddUser() {
    setUserForm({ ...EMPTY_USER });
    setUserModal({ mode: "add" });
  }

  function openEditUser(user) {
    setUserForm({ email: user.email, role: user.role });
    setUserModal({ mode: "edit", originalEmail: user.email });
  }

  function applyUserForm() {
    const email = userForm.email.trim().toLowerCase();
    if (!email) return;
    const nextUsers = [...users];
    if (userModal.mode === "edit") {
      const idx = nextUsers.findIndex(u => u.email.toLowerCase() === userModal.originalEmail.toLowerCase());
      if (idx !== -1) {
        nextUsers[idx] = { email, role: userForm.role };
        queueAudit("update", "user", `Usuário ${email} definido como ${roleLabel(userForm.role)}`);
      }
    } else {
      if (nextUsers.some(u => u.email.toLowerCase() === email)) {
        window.alert("Este e-mail já está cadastrado.");
        return;
      }
      nextUsers.push({ email, role: userForm.role });
      queueAudit("create", "user", `Usuário ${email} adicionado como ${roleLabel(userForm.role)}`);
    }
    setUsers(nextUsers);
    setUserModal(null);
  }

  function deleteUser(user) {
    if (!window.confirm(`Remover o usuário ${user.email}?`)) return;
    queueAudit("delete", "user", `Usuário ${user.email} removido`);
    setUsers(users.filter(u => u.email.toLowerCase() !== user.email.toLowerCase()));
  }

  function openAddLink() {
    setLinkForm({ ...EMPTY_LINK, tab: allSections[0]?.id || "" });
    setLinkModal({ mode: "add" });
  }

  function openEditLink(item, sectionId, access) {
    setLinkForm({ ...item, tab: sectionId, access });
    setLinkModal({ mode: "edit", itemId: item.id, section: sectionId, access });
  }

  function applyLinkForm() {
    const nd = clone(data);
    const targetList = linkForm.access === "restricted" ? nd.restricted : nd.public;
    const section = targetList.find(s => s.id === linkForm.tab);
    if (!section) return;
    const item = {
      id: linkModal.mode === "add" ? Date.now().toString() : linkModal.itemId,
      title: linkForm.title, desc: linkForm.desc, url: linkForm.url, color: linkForm.color,
      subtab: linkForm.subtab || "",
    };
    if (linkModal.mode === "edit") {
      const oldList = linkModal.access === "restricted" ? nd.restricted : nd.public;
      const oldSec  = oldList.find(s => s.id === linkModal.section);
      if (oldSec) oldSec.items = oldSec.items.filter(i => i.id !== linkModal.itemId);
      queueAudit("update", "link", `Link "${item.title}" editado (${section.label})`);
    } else {
      queueAudit("create", "link", `Link "${item.title}" adicionado em ${section.label}`);
    }
    section.items.push(item);
    setData(nd); setLinkModal(null);
  }

  function deleteLink(itemId, sectionId, access) {
    if (!window.confirm("Remover este link?")) return;
    const nd = clone(data);
    const list = access === "restricted" ? nd.restricted : nd.public;
    const sec  = list.find(s => s.id === sectionId);
    const removed = sec?.items.find(i => i.id === itemId);
    if (sec) sec.items = sec.items.filter(i => i.id !== itemId);
    if (removed) queueAudit("delete", "link", `Link "${removed.title}" removido de ${sec.label}`);
    setData(nd);
  }

  function openAddTab() {
    setTabForm({ ...EMPTY_TAB });
    setTabModal({ mode: "add" });
  }

  function openEditTab(sec) {
    setTabForm({ label: sec.label, access: sec.access, icon: sec.icon || "" });
    setTabModal({ mode: "edit", id: sec.id, prevAccess: sec.access });
  }

  function applyTabForm() {
    const nd = clone(data);
    const icon = tabForm.icon || "";
    if (tabModal.mode === "add") {
      const newSec = { id: slugify(tabForm.label) + "_" + Date.now(), label: tabForm.label, icon, items: [] };
      if (tabForm.access === "restricted") nd.restricted.push(newSec);
      else nd.public.push(newSec);
      queueAudit("create", "tab", `Aba "${tabForm.label}" criada (${tabForm.access === "restricted" ? "restrita" : "pública"})`);
    } else {
      const prevList = tabModal.prevAccess === "restricted" ? nd.restricted : nd.public;
      const idx = prevList.findIndex(s => s.id === tabModal.id);
      if (idx !== -1) {
        const existing = prevList[idx];
        if (tabForm.access !== tabModal.prevAccess) {
          prevList.splice(idx, 1);
          const newList = tabForm.access === "restricted" ? nd.restricted : nd.public;
          newList.push({ ...existing, label: tabForm.label, icon });
        } else {
          prevList[idx] = { ...existing, label: tabForm.label, icon };
        }
        queueAudit("update", "tab", `Aba "${tabForm.label}" atualizada`);
      }
    }
    setData(nd); setTabModal(null);
  }

  function deleteTab(sec) {
    if (sec.items.length > 0 && !window.confirm(`A aba "${sec.label}" tem ${sec.items.length} link(s). Remover mesmo assim?`)) return;
    const nd = clone(data);
    if (sec.access === "restricted") nd.restricted = nd.restricted.filter(s => s.id !== sec.id);
    else nd.public = nd.public.filter(s => s.id !== sec.id);
    queueAudit("delete", "tab", `Aba "${sec.label}" removida`);
    setData(nd);
  }

  // ── Categorias (sub-abas) ──────────────────────────────────
  function updateCategories(nextList) {
    setSettings(s => ({ ...s, categories: nextList }));
  }

  function addCategory() {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    if (categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
      window.alert(`A categoria "${trimmed}" já existe.`);
      return;
    }
    updateCategories([...categories, trimmed]);
    queueAudit("update", "settings", `Categoria "${trimmed}" adicionada`);
    setNewCategory("");
  }

  function removeCategory(cat) {
    const usedInLinks = allSections.some(sec => (sec.items || []).some(i => i.subtab === cat));
    const usedInAnnouncements = announcements.some(a => a.subtab === cat);
    let warning = `Remover a categoria "${cat}"?`;
    if (usedInLinks || usedInAnnouncements) {
      warning += "\n\nRecados e links que já usam essa categoria não serão apagados — apenas deixam de ter a pílula de filtro (continuam aparecendo em \"Todos\").";
    }
    if (!window.confirm(warning)) return;
    updateCategories(categories.filter(c => c !== cat));
    queueAudit("update", "settings", `Categoria "${cat}" removida`);
  }

  function moveCategory(cat, delta) {
    const idx = categories.indexOf(cat);
    const newIdx = idx + delta;
    if (idx === -1 || newIdx < 0 || newIdx >= categories.length) return;
    const next = [...categories];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    updateCategories(next);
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-top">
          <h2>Administração</h2>
          <div className="admin-actions">
            {view === "links" && <button className="btn-primary" onClick={openAddLink}>+ Novo link</button>}
            {view === "tabs"  && <button className="btn-primary" onClick={openAddTab}>+ Nova aba</button>}
            {view === "users" && <button className="btn-primary" onClick={openAddUser}>+ Novo usuário</button>}
            {view === "announcements" && <button className="btn-primary" onClick={openAddAnnouncement}>+ Novo recado</button>}
            {view !== "manual" && view !== "logs" && (
              <button
                className="btn-primary"
                style={{ background: saving ? "#888" : saved ? "#0F6E56" : undefined }}
                onClick={handleSave} disabled={saving}
              >
                {saving ? "Salvando…" : saved ? "Salvo!" : "Salvar alterações"}
              </button>
            )}
          </div>
        </div>
        <div className="admin-view-toggle">
          <button className={view === "announcements" ? "active" : ""} onClick={() => setView("announcements")}>Recados</button>
          <button className={view === "links" ? "active" : ""} onClick={() => setView("links")}>Links</button>
          <button className={view === "tabs"  ? "active" : ""} onClick={() => setView("tabs")}>Abas</button>
          <button className={view === "users" ? "active" : ""} onClick={() => setView("users")}>Usuários</button>
          <button className={view === "settings" ? "active" : ""} onClick={() => setView("settings")}>Configurações</button>
        </div>
        <div className="admin-view-toggle admin-view-toggle-secondary">
          <button className={view === "logs" ? "active" : ""} onClick={() => setView("logs")}>Logs</button>
          <button className={view === "manual" ? "active" : ""} onClick={() => setView("manual")}>Manual</button>
        </div>
      </div>

      {view === "links" && allSections.map(sec => (
        <div key={sec.id}>
          <div className="admin-section-title">
            {sec.label}
            <span className={`pill ${sec.access === "restricted" ? "pill-restricted" : "pill-public"}`}>
              {sec.access === "restricted" ? "Líderes" : "Todos"}
            </span>
          </div>
          <table className="admin-table" style={{ marginBottom: 16 }}>
            <thead><tr><th>Nome</th><th>Descrição</th><th>Sub-aba</th><th>URL</th><th>Cor</th><th></th></tr></thead>
            <tbody>
              {sec.items.length === 0 && (
                <tr><td colSpan={6} style={{ color: "var(--text3)", fontStyle: "italic" }}>Nenhum link nesta aba</td></tr>
              )}
              {[...sec.items].sort(sortByTitle).map(item => {
                const col = COLOR_OPTIONS.find(c => c.value === item.color) || COLOR_OPTIONS[0];
                return (
                  <tr key={item.id}>
                    <td style={{ fontWeight: 500 }}>{item.title}</td>
                    <td style={{ color: "var(--text2)" }}>{item.desc}</td>
                    <td style={{ color: item.subtab ? "var(--text)" : "var(--text3)", fontSize: 12 }}>
                      {item.subtab || "—"}
                    </td>
                    <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <a href={item.url} target="_blank" rel="noreferrer" style={{ color: "var(--blue)", fontSize: 12 }}>{item.url}</a>
                    </td>
                    <td>
                      <span className="color-pill" style={{ background: col.bg, color: col.fg }}>
                        <span className="color-dot" style={{ background: col.fg }}></span>
                        {col.label}
                      </span>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn-secondary" onClick={() => openEditLink(item, sec.id, sec.access)}>Editar</button>
                        <button className="btn-danger"    onClick={() => deleteLink(item.id, sec.id, sec.access)}>Remover</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {view === "tabs" && (
        <>
          <div className="admin-section-title">
            Abas públicas <span className="pill pill-public">Todos</span>
          </div>
          <TabTable sections={data.public.map(s => ({ ...s, access: "public" }))} onEdit={openEditTab} onDelete={deleteTab} />

          <div className="admin-section-title" style={{ marginTop: 24 }}>
            Abas restritas <span className="pill pill-restricted">Líderes</span>
          </div>
          <TabTable sections={data.restricted.map(s => ({ ...s, access: "restricted" }))} onEdit={openEditTab} onDelete={deleteTab} />
        </>
      )}

      {view === "announcements" && (
        <>
          <div className="admin-section-title">
            Recados da Home
          </div>
          <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 12 }}>
            Os 30 recados mais recentes e não expirados aparecem na aba Home, na <strong>ordem de prioridade</strong> (1 = canto superior esquerdo, 2 ao lado, e assim por diante).
            Use as setas ▲▼ para reordenar ou defina a prioridade manualmente ao editar. Recados expirados são removidos automaticamente.
          </p>
          {announcements.length > 1 && (
            <button className="btn-secondary" style={{ marginBottom: 10 }} onClick={normalizePriorities}>
              Renumerar prioridades (1, 2, 3…)
            </button>
          )}
          <table className="admin-table" style={{ marginBottom: 16 }}>
            <thead><tr><th style={{ width: 60 }}>#</th><th style={{ width: 50 }} title="Fixar na lateral direita">📌</th><th>Imagem</th><th>Título</th><th>Texto</th><th>Sub-aba</th><th>Criado em</th><th>Expira em</th><th></th></tr></thead>
            <tbody>
              {announcements.length === 0 && (
                <tr><td colSpan={9} style={{ color: "var(--text3)", fontStyle: "italic" }}>Nenhum recado cadastrado.</td></tr>
              )}
              {(() => {
                const sorted = [...announcements].sort(comparePriority);
                return sorted.map((a, index) => {
                  const expired = a.expiresAt && new Date(a.expiresAt) <= new Date();
                  // Compat: formato antigo (type + content)
                  const text  = a.text  != null ? a.text  : (a.type === "text"  ? (a.content || "") : "");
                  const image = a.imageUrl != null ? a.imageUrl : (a.type === "image" ? (a.content || "") : "");
                  return (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 600, color: "var(--blue)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ minWidth: 18 }}>{a.priority || "—"}</span>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <button
                              className="btn-secondary"
                              style={{ padding: "1px 5px", fontSize: 10, lineHeight: 1 }}
                              disabled={index === 0}
                              onClick={() => moveAnnouncement(a, -1)}
                              title="Subir"
                            >▲</button>
                            <button
                              className="btn-secondary"
                              style={{ padding: "1px 5px", fontSize: 10, lineHeight: 1 }}
                              disabled={index === sorted.length - 1}
                              onClick={() => moveAnnouncement(a, 1)}
                              title="Descer"
                            >▼</button>
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="btn-secondary"
                          onClick={() => togglePinned(a)}
                          title={a.pinned ? "Desafixar" : "Fixar na lateral"}
                          style={{
                            padding: "4px 8px",
                            fontSize: 14,
                            background: a.pinned ? "var(--amber-bg)" : "var(--surface2)",
                            color: a.pinned ? "var(--amber)" : "var(--text3)",
                            border: a.pinned ? "0.5px solid var(--amber)" : "0.5px solid var(--border)",
                          }}
                        >📌</button>
                      </td>
                      <td>
                        {image ? (
                          <img src={image} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
                        ) : (
                          <span style={{ color: "var(--text3)", fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 500 }}>{a.title || "—"}</td>
                      <td style={{ color: "var(--text2)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {text || <span style={{ color: "var(--text3)" }}>—</span>}
                      </td>
                      <td style={{ color: a.subtab ? "var(--text)" : "var(--text3)", fontSize: 12 }}>
                        {a.subtab || "—"}
                      </td>
                      <td style={{ color: "var(--text3)", fontSize: 12 }}>{formatDate(a.createdAt)}</td>
                      <td style={{ fontSize: 12 }}>
                        {a.expiresAt ? (
                          <span className={expired ? "pill expired-pill" : ""} style={{ color: expired ? undefined : "var(--text3)" }}>
                            {expired ? "Expirado" : formatDate(a.expiresAt)}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text3)" }}>Sem expiração</span>
                        )}
                      </td>
                      <td style={{ verticalAlign: "middle" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn-secondary" onClick={() => openEditAnnouncement(a)}>Editar</button>
                          <button className="btn-danger"    onClick={() => deleteAnnouncement(a)}>Remover</button>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </>
      )}

      {view === "manual" && <ManualView />}

      {view === "logs" && <LogsView />}

      {view === "settings" && (
        <>
          <div className="admin-section-title">Logo da empresa</div>
          <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 12 }}>
            Imagem exibida no canto superior esquerdo da sidebar. Recomendado: PNG com fundo transparente, horizontal.
            Deixe em branco para voltar ao logo padrão.
          </p>
          <div style={{ maxWidth: 420 }}>
            <ImageDropzone
              value={settings.logoUrl || ""}
              onChange={url => {
                setSettings(s => ({ ...s, logoUrl: url }));
                queueAudit("update", "settings", url ? "Logo da empresa atualizada" : "Logo da empresa removida (voltou ao padrão)");
              }}
              folder="branding"
            />
          </div>

          <div className="admin-section-title" style={{ marginTop: 32 }}>Categorias dos recados e links</div>
          <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 12, maxWidth: 620 }}>
            São as opções de <strong>sub-aba</strong> que aparecem ao criar um recado ou um link, e viram as pílulas
            de filtro na Home e nas abas. A ordem aqui define a ordem das pílulas. Não esqueça de clicar em
            <strong> Salvar alterações</strong> depois de mexer.
          </p>
          <table className="admin-table" style={{ marginBottom: 16, maxWidth: 620 }}>
            <thead><tr><th>Categoria</th><th style={{ width: 160 }}>Ordem</th><th style={{ width: 110 }}></th></tr></thead>
            <tbody>
              {categories.length === 0 && (
                <tr><td colSpan={3} style={{ color: "var(--text3)", fontStyle: "italic" }}>Nenhuma categoria cadastrada.</td></tr>
              )}
              {categories.map((cat, i) => (
                <tr key={cat}>
                  <td style={{ fontWeight: 500 }}>{cat}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-secondary" onClick={() => moveCategory(cat, -1)} disabled={i === 0} title="Subir">↑</button>
                      <button className="btn-secondary" onClick={() => moveCategory(cat, 1)} disabled={i === categories.length - 1} title="Descer">↓</button>
                    </div>
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <button className="btn-danger" onClick={() => removeCategory(cat)}>Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 8, maxWidth: 620 }}>
            <input
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
              placeholder="Nova categoria (ex: Financeiro)"
              style={{ flex: 1 }}
            />
            <button className="btn-primary" onClick={addCategory} disabled={!newCategory.trim()}>+ Adicionar</button>
          </div>
        </>
      )}

      {view === "users" && (
        <>
          <div className="admin-section-title">
            Usuários e permissões
          </div>
          <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 12 }}>
            Usuários não listados aqui entram como <strong>Usuário</strong> por padrão.
          </p>
          <table className="admin-table" style={{ marginBottom: 16 }}>
            <thead><tr><th>E-mail</th><th>Perfil</th><th></th></tr></thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={3} style={{ color: "var(--text3)", fontStyle: "italic" }}>Nenhum usuário cadastrado.</td></tr>
              )}
              {users.map(u => (
                <tr key={u.email}>
                  <td style={{ fontWeight: 500 }}>{u.email}</td>
                  <td>
                    <span className={`pill pill-${u.role === "admin" ? "restricted" : u.role === "lider" ? "public" : "public"}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-secondary" onClick={() => openEditUser(u)}>Editar</button>
                      <button className="btn-danger"    onClick={() => deleteUser(u)}>Remover</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {linkModal && (
        <Modal title={linkModal.mode === "add" ? "Adicionar link" : "Editar link"} onClose={() => setLinkModal(null)}>
          <Field label="Nome">
            <input value={linkForm.title} onChange={e => setLinkForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: PowerTickets" />
          </Field>
          <Field label="Descrição">
            <input value={linkForm.desc} onChange={e => setLinkForm(f => ({ ...f, desc: e.target.value }))} placeholder="Breve descrição da ferramenta" />
          </Field>
          <Field label="URL">
            <input value={linkForm.url} onChange={e => setLinkForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." />
          </Field>
          <Field label="Aba de destino">
            <select value={linkForm.tab} onChange={e => {
              const sec = allSections.find(s => s.id === e.target.value);
              setLinkForm(f => ({ ...f, tab: e.target.value, access: sec?.access || "public" }));
            }}>
              {allSections.map(s => (
                <option key={s.id} value={s.id}>{s.label} — {s.access === "restricted" ? "Líderes" : "Todos"}</option>
              ))}
            </select>
          </Field>
          <Field label="Sub-aba (opcional)">
            <select
              value={linkForm.subtab || ""}
              onChange={e => setLinkForm(f => ({ ...f, subtab: e.target.value }))}
            >
              <option value="">— Sem categoria —</option>
              {categories.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
              {linkForm.subtab && !categories.includes(linkForm.subtab) && (
                <option value={linkForm.subtab}>{linkForm.subtab} (categoria removida)</option>
              )}
            </select>
          </Field>
          <Field label="Cor do ícone">
            <ColorPicker value={linkForm.color} onChange={c => setLinkForm(f => ({ ...f, color: c }))} />
          </Field>
          <ModalActions onCancel={() => setLinkModal(null)} onConfirm={applyLinkForm} />
        </Modal>
      )}

      {announcementModal && (
        <Modal title={announcementModal.mode === "add" ? "Novo recado" : "Editar recado"} onClose={() => setAnnouncementModal(null)} dismissable={false}>
          <Field label="Título (opcional)">
            <input
              value={announcementForm.title}
              onChange={e => setAnnouncementForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Ex: Feriado de Corpus Christi"
            />
          </Field>
          <Field label="Texto (opcional)">
            <textarea
              value={announcementForm.text}
              onChange={e => setAnnouncementForm(f => ({ ...f, text: e.target.value }))}
              placeholder="Digite o recado…"
              rows={10}
              style={{ width: "100%", padding: "10px 12px", border: "0.5px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 140 }}
            />
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4, textAlign: "right" }}>
              {announcementForm.text.length} caracteres
            </div>
          </Field>
          <Field label="Imagem (opcional)">
            <ImageDropzone
              value={announcementForm.imageUrl}
              onChange={url => setAnnouncementForm(f => ({ ...f, imageUrl: url }))}
            />
          </Field>
          <Field label="Link (opcional) — abre em nova aba ao clicar no recado">
            <input
              type="url"
              value={announcementForm.linkUrl}
              onChange={e => setAnnouncementForm(f => ({ ...f, linkUrl: e.target.value }))}
              placeholder="https://exemplo.com/pagina"
            />
          </Field>
          <Field label="Sub-aba (opcional) — categoria do recado na Home">
            <select
              value={announcementForm.subtab || ""}
              onChange={e => setAnnouncementForm(f => ({ ...f, subtab: e.target.value }))}
            >
              <option value="">— Sem categoria —</option>
              {categories.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
              {announcementForm.subtab && !categories.includes(announcementForm.subtab) && (
                <option value={announcementForm.subtab}>{announcementForm.subtab} (categoria removida)</option>
              )}
            </select>
          </Field>
          <Field label="Prioridade (ordem na Home — 1 aparece primeiro)">
            <input
              type="number"
              min={1}
              value={announcementForm.priority || ""}
              onChange={e => setAnnouncementForm(f => ({ ...f, priority: e.target.value }))}
              placeholder="1"
              style={{ width: 120 }}
            />
          </Field>
          <div className="form-row">
            <Field label="Data de cadastro">
              <input
                type="datetime-local"
                value={announcementForm.createdAt}
                onChange={e => setAnnouncementForm(f => ({ ...f, createdAt: e.target.value }))}
              />
            </Field>
            <Field label="Expira em (obrigatório)">
              {announcementForm.pinned ? (
                <div style={{ fontSize: 12, color: "var(--text2)", padding: "10px 12px", background: "var(--surface2)", border: "0.5px solid var(--border)", borderRadius: "var(--radius-md)" }}>
                  Recado fixo — expira automaticamente em 10 anos.
                </div>
              ) : (
                <input
                  type="datetime-local"
                  value={announcementForm.expiresAt}
                  onChange={e => setAnnouncementForm(f => ({ ...f, expiresAt: e.target.value }))}
                />
              )}
            </Field>
          </div>
          <Field label="">
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", padding: "8px 10px", background: "var(--surface2)", border: "0.5px solid var(--border)", borderRadius: "var(--radius-md)" }}>
              <input
                type="checkbox"
                checked={!!announcementForm.pinned}
                onChange={e => setAnnouncementForm(f => ({ ...f, pinned: e.target.checked }))}
                style={{ width: "auto" }}
              />
              <span>📌 Fixar na lateral direita do portal (até {PINNED_LIMIT} recados)</span>
            </label>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
              Atualmente {countPinned(announcementModal.mode === "edit" ? announcementModal.id : null)} de {PINNED_LIMIT} recados fixados.
            </div>
          </Field>
          <p style={{ fontSize: 11, color: "var(--text3)", marginTop: -4, marginBottom: 8 }}>
            Preencha pelo menos o texto ou a imagem. A data de expiração é obrigatória — recados fixos expiram em 10 anos automaticamente. Recados são removidos na próxima carga do portal após a data indicada.
          </p>
          <ModalActions
            onCancel={() => setAnnouncementModal(null)}
            onConfirm={applyAnnouncementForm}
            disabled={
              (!announcementForm.text.trim() && !announcementForm.imageUrl.trim()) ||
              (!announcementForm.pinned && !announcementForm.expiresAt)
            }
          />
        </Modal>
      )}

      {userModal && (
        <Modal title={userModal.mode === "add" ? "Novo usuário" : "Editar usuário"} onClose={() => setUserModal(null)}>
          <Field label="E-mail">
            <input
              type="email"
              value={userForm.email}
              onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))}
              placeholder="usuario@powertuning.com.br"
            />
          </Field>
          <Field label="Perfil">
            <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <ModalActions onCancel={() => setUserModal(null)} onConfirm={applyUserForm} disabled={!userForm.email.trim()} />
        </Modal>
      )}

      {tabModal && (
        <Modal title={tabModal.mode === "add" ? "Nova aba" : "Editar aba"} onClose={() => setTabModal(null)}>
          <Field label="Nome da aba">
            <input value={tabForm.label} onChange={e => setTabForm(f => ({ ...f, label: e.target.value }))} placeholder="Ex: Financeiro" />
          </Field>
          <Field label="Visibilidade">
            <div className="access-toggle">
              <button
                className={tabForm.access === "public" ? "active" : ""}
                onClick={() => setTabForm(f => ({ ...f, access: "public" }))}
              >
                Todos os usuários
              </button>
              <button
                className={tabForm.access === "restricted" ? "active restricted" : ""}
                onClick={() => setTabForm(f => ({ ...f, access: "restricted" }))}
              >
                Somente líderes
              </button>
            </div>
          </Field>
          <Field label="Ícone">
            <div className="tab-icon-picker">
              {TAB_ICON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`tab-icon-option${tabForm.icon === opt.value ? " selected" : ""}`}
                  onClick={() => setTabForm(f => ({ ...f, icon: opt.value }))}
                  title={opt.hint}
                >
                  <TabIcon name={opt.value} />
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>
              Sem seleção, usa <strong>pasta</strong> como padrão.
            </div>
          </Field>
          <ModalActions onCancel={() => setTabModal(null)} onConfirm={applyTabForm} disabled={!tabForm.label.trim()} />
        </Modal>
      )}
    </div>
  );
}

function TabTable({ sections, onEdit, onDelete }) {
  if (sections.length === 0)
    return <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 16 }}>Nenhuma aba nesta categoria.</p>;
  return (
    <table className="admin-table" style={{ marginBottom: 8 }}>
      <thead><tr><th style={{ width: 50 }}>Ícone</th><th>Nome da aba</th><th>Links cadastrados</th><th></th></tr></thead>
      <tbody>
        {sections.map(sec => (
          <tr key={sec.id}>
            <td style={{ width: 50 }}>
              <span className="tab-icon-preview" title={sec.icon || "automático"}>
                <TabIcon name={resolveTabIcon(sec)} />
              </span>
            </td>
            <td style={{ fontWeight: 500 }}>{sec.label}</td>
            <td style={{ color: "var(--text2)" }}>{sec.items.length} link{sec.items.length !== 1 ? "s" : ""}</td>
            <td style={{ verticalAlign: "middle" }}>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn-secondary" onClick={() => onEdit(sec)}>Editar</button>
                <button className="btn-danger"    onClick={() => onDelete(sec)}>Remover</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div className="color-picker">
      {COLOR_OPTIONS.map(c => (
        <button
          key={c.value}
          className={`color-swatch${value === c.value ? " selected" : ""}`}
          style={{ background: c.bg, borderColor: value === c.value ? c.fg : "transparent" }}
          onClick={() => onChange(c.value)}
          title={c.label}
        >
          <span className="color-dot" style={{ background: c.fg }}></span>
          <span style={{ fontSize: 11, color: c.fg, fontWeight: value === c.value ? 500 : 400 }}>{c.label}</span>
        </button>
      ))}
    </div>
  );
}

function Modal({ title, onClose, children, dismissable = true }) {
  // dismissable=false impede fechar clicando fora do formulário (evita perder o que
  // já foi digitado). Nesse caso só os botões Cancelar/Confirmar fecham o modal.
  return (
    <div className="modal-overlay" onClick={dismissable ? onClose : undefined}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ onCancel, onConfirm, disabled }) {
  return (
    <div className="modal-actions">
      <button className="btn-secondary" onClick={onCancel}>Cancelar</button>
      <button className="btn-primary" onClick={onConfirm} disabled={disabled}>Confirmar</button>
    </div>
  );
}

function ImageDropzone({ value, onChange, folder = "announcements" }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Arquivo inválido: envie uma imagem.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const url = await uploadImage(file, folder);
      onChange(url);
    } catch (err) {
      console.error(err);
      setError("Falha ao enviar a imagem. Tente novamente.");
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function onPaste(e) {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) { handleFile(file); e.preventDefault(); return; }
      }
    }
  }

  function openPicker() {
    if (inputRef.current) inputRef.current.click();
  }

  function removeImage(e) {
    e.stopPropagation();
    onChange("");
  }

  return (
    <div>
      <div
        className={`image-dropzone${dragging ? " dragging" : ""}${value ? " has-image" : ""}`}
        onClick={openPicker}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onPaste={onPaste}
        tabIndex={0}
      >
        {value ? (
          <>
            <img src={value} alt="pré-visualização" className="image-dropzone-preview" />
            <button type="button" className="image-dropzone-remove" onClick={removeImage} title="Remover imagem">×</button>
          </>
        ) : (
          <>
            <div className="image-dropzone-label">
              {uploading ? "Enviando…" : "Clique, arraste ou cole uma imagem aqui"}
            </div>
            <div className="image-dropzone-hint">PNG, JPG, GIF — máx. ~5 MB</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={e => handleFile(e.target.files && e.target.files[0])}
        />
      </div>
      {uploading && <div className="image-dropzone-uploading">Enviando imagem para o blob…</div>}
      {error && <div style={{ fontSize: 12, color: "#a32d2d", marginTop: 6 }}>{error}</div>}
      {value && (
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6, wordBreak: "break-all" }}>
          URL: <a href={value} target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>{value}</a>
        </div>
      )}
    </div>
  );
}

function LogsView() {
  const [accessLog, setAccessLog] = useState(null);   // { accesses: {...} } | null
  const [auditLog, setAuditLog]   = useState(null);   // { events: [...] }  | null
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [accessFilter, setAccessFilter] = useState("");
  const [auditFilter,  setAuditFilter]  = useState("");

  async function reload() {
    setLoading(true); setError(null);
    try {
      const [access, audit] = await Promise.all([loadAccessLog(), loadAuditLog()]);
      setAccessLog(access);
      setAuditLog(audit);
    } catch (err) {
      setError(err?.message || "Erro ao carregar logs");
    }
    setLoading(false);
  }

  useEffect(() => { reload(); }, []);

  if (loading) return <p style={{ color: "var(--text3)", fontSize: 13 }}>Carregando logs…</p>;
  if (error) return <p style={{ color: "#a32d2d", fontSize: 13 }}>Erro: {error}</p>;

  // ── Acessos ─────────────────────────────────────────────
  // Ordenado alfabeticamente pelo nome (com fallback no e-mail quando faltar).
  const accessRows = Object.values(accessLog?.accesses || {})
    .sort((a, b) => {
      const ka = (a.name || a.email || "").toLowerCase();
      const kb = (b.name || b.email || "").toLowerCase();
      return ka.localeCompare(kb, "pt-BR");
    });
  const accessFiltered = accessFilter
    ? accessRows.filter(r =>
        (r.email || "").toLowerCase().includes(accessFilter.toLowerCase()) ||
        (r.name  || "").toLowerCase().includes(accessFilter.toLowerCase()))
    : accessRows;

  // ── Auditoria ──────────────────────────────────────────
  const auditEvents = auditLog?.events || [];
  const auditFiltered = auditFilter
    ? auditEvents.filter(e =>
        (e.user    || "").toLowerCase().includes(auditFilter.toLowerCase()) ||
        (e.details || "").toLowerCase().includes(auditFilter.toLowerCase()) ||
        (e.target  || "").toLowerCase().includes(auditFilter.toLowerCase()) ||
        (e.action  || "").toLowerCase().includes(auditFilter.toLowerCase()))
    : auditEvents;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 12 }}>
          <div className="admin-section-title" style={{ margin: 0 }}>
            Últimos acessos
            <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 400, marginLeft: 10 }}>
              ({accessRows.length} usuário{accessRows.length !== 1 ? "s" : ""})
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder="Filtrar por nome ou e-mail…"
              value={accessFilter}
              onChange={e => setAccessFilter(e.target.value)}
              style={{ fontSize: 12, padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: "var(--radius-md)", minWidth: 220 }}
            />
            <button className="btn-secondary" onClick={reload}>Atualizar</button>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 10 }}>
          Data do último login de cada usuário. Registro atualizado a cada autenticação.
        </p>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th style={{ width: 90, textAlign: "right" }}>Acessos</th>
              <th>Último acesso</th>
            </tr>
          </thead>
          <tbody>
            {accessFiltered.length === 0 ? (
              <tr><td colSpan={4} style={{ color: "var(--text3)", fontStyle: "italic" }}>
                {accessRows.length === 0 ? "Nenhum acesso registrado ainda." : "Nenhum resultado para o filtro."}
              </td></tr>
            ) : accessFiltered.map(r => (
              <tr key={r.email}>
                <td style={{ fontWeight: 500 }}>{r.name || "—"}</td>
                <td style={{ color: "var(--text2)" }}>{r.email}</td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text2)" }}>{r.count || 1}</td>
                <td style={{ color: "var(--text2)", fontSize: 12 }}>{formatDate(r.lastAccess)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 12 }}>
          <div className="admin-section-title" style={{ margin: 0 }}>
            Alterações (últimos 15 dias)
            <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 400, marginLeft: 10 }}>
              ({auditEvents.length} evento{auditEvents.length !== 1 ? "s" : ""})
            </span>
          </div>
          <input
            type="text"
            placeholder="Filtrar por usuário, ação ou descrição…"
            value={auditFilter}
            onChange={e => setAuditFilter(e.target.value)}
            style={{ fontSize: 12, padding: "6px 10px", border: "0.5px solid var(--border)", borderRadius: "var(--radius-md)", minWidth: 260 }}
          />
        </div>
        <p style={{ fontSize: 12, color: "var(--text3)", marginBottom: 10 }}>
          Registros são mantidos por 15 dias e só são persistidos quando o administrador clica em <strong>Salvar alterações</strong>.
        </p>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Data</th>
              <th>Usuário</th>
              <th style={{ width: 80 }}>Ação</th>
              <th style={{ width: 110 }}>Alvo</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>
            {auditFiltered.length === 0 ? (
              <tr><td colSpan={5} style={{ color: "var(--text3)", fontStyle: "italic" }}>
                {auditEvents.length === 0 ? "Nenhuma alteração registrada nos últimos 15 dias." : "Nenhum resultado para o filtro."}
              </td></tr>
            ) : auditFiltered.map((e, i) => (
              <tr key={i}>
                <td style={{ color: "var(--text2)", fontSize: 12 }}>{formatDate(e.timestamp)}</td>
                <td style={{ color: "var(--text2)", fontSize: 12 }}>{e.user || "—"}</td>
                <td><span className={`pill pill-${actionPillClass(e.action)}`}>{actionLabel(e.action)}</span></td>
                <td style={{ color: "var(--text2)", fontSize: 12 }}>{targetLabel(e.target)}</td>
                <td>{e.details || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function actionLabel(a) {
  if (a === "create") return "Criar";
  if (a === "update") return "Editar";
  if (a === "delete") return "Remover";
  return a || "—";
}
function actionPillClass(a) {
  if (a === "create") return "public";
  if (a === "update") return "public";
  if (a === "delete") return "restricted";
  return "public";
}
function targetLabel(t) {
  if (t === "link") return "Link";
  if (t === "tab") return "Aba";
  if (t === "user") return "Usuário";
  if (t === "announcement") return "Recado";
  if (t === "settings") return "Configurações";
  return t || "—";
}

function ManualView() {
  return (
    <div className="manual-view">
      <div className="manual-hero">
        <h2>Manual da área administrativa</h2>
        <p>Guia rápido para manter o portal atualizado. Todas as alterações ficam disponíveis para todos os usuários assim que você clicar em <strong>Salvar alterações</strong>.</p>
      </div>

      <nav className="manual-toc">
        <strong>Nesta página:</strong>
        <a href="#manual-geral">Antes de começar</a>
        <a href="#manual-recados">Criar e organizar recados</a>
        <a href="#manual-reacoes">Reações e sino de notificações</a>
        <a href="#manual-abas">Gerenciar abas</a>
        <a href="#manual-links">Gerenciar links</a>
        <a href="#manual-usuarios">Permissões de usuários</a>
        <a href="#manual-config">Logo e categorias</a>
        <a href="#manual-logs">Logs e auditoria</a>
        <a href="#manual-dicas">Dicas e solução de problemas</a>
      </nav>

      <section id="manual-geral" className="manual-section">
        <h3>Antes de começar</h3>
        <p>A área administrativa fica dividida em abas no topo:</p>
        <ul>
          <li><strong>Recados</strong> — comunicados exibidos na Home do portal.</li>
          <li><strong>Links</strong> — cards (ferramentas, sistemas, documentos) que aparecem dentro de cada aba lateral.</li>
          <li><strong>Abas</strong> — as seções da barra lateral (Geral e Restrito).</li>
          <li><strong>Usuários</strong> — perfis de acesso por e-mail (admin, líder, usuário).</li>
          <li><strong>Configurações</strong> — logo da empresa e as categorias (sub-abas) de recados e links.</li>
          <li><strong>Logs</strong> — último acesso de cada usuário e histórico de alterações dos últimos 15 dias.</li>
          <li><strong>Manual</strong> — este guia.</li>
        </ul>
        <div className="manual-callout">
          <strong>Regra de ouro:</strong> mudanças só vão pro ar depois que você clicar em <strong>Salvar alterações</strong> (botão azul no canto superior direito). Se sair da aba sem salvar, as alterações daquela aba são perdidas.
        </div>
        <p><strong>Cada aba salva separadamente.</strong> Se você editar um recado e depois ir para Links sem salvar, o recado não vai pro ar. Salve antes de trocar de aba.</p>
      </section>

      <section id="manual-recados" className="manual-section">
        <h3>Criar e organizar recados</h3>
        <p>Recados são os cards que aparecem na Home. Cada recado pode ter título, texto e/ou imagem, e é agendado pra expirar numa data (obrigatória).</p>

        <h4>Criar um recado novo</h4>
        <ol>
          <li>Abra a aba <strong>Recados</strong> e clique em <strong>+ Novo recado</strong>.</li>
          <li>Preencha os campos que fizerem sentido:
            <ul>
              <li><strong>Título</strong> (opcional) — aparece em destaque no card.</li>
              <li><strong>Texto</strong> (opcional) — aceita múltiplas linhas. Pode colar links, e-mails, tudo em texto puro.</li>
              <li><strong>Imagem</strong> (opcional) — três formas de anexar:
                <ul>
                  <li>Clicar no retângulo pontilhado e escolher do computador;</li>
                  <li>Arrastar e soltar um arquivo em cima;</li>
                  <li>Copiar uma imagem (Print Screen, recorte) e colar com <strong>Ctrl+V</strong> em cima do retângulo.</li>
                </ul>
              </li>
              <li><strong>Link</strong> (opcional) — endereço que vira um botão <em>Abrir link</em> no card. Ao clicar, abre em uma nova aba do navegador. Útil para apontar para formulários, documentação, cursos, etc.</li>
              <li><strong>Sub-aba</strong> (opcional) — categoria do recado na Home. As opções saem da lista cadastrada em <strong>Configurações → Categorias</strong> (você mesmo cria e ordena). Se pelo menos um recado tiver categoria, aparece uma barra de pílulas na Home que filtra por tipo. Deixe "Sem categoria" se for um recado geral.</li>
              <li><strong>Prioridade</strong> — número que define a ordem no Home (1 é o primeiro, canto superior esquerdo).</li>
              <li><strong>Data de cadastro</strong> — preenchido automaticamente com a data atual. Pode ajustar se quiser.</li>
              <li><strong>Expira em</strong> (obrigatório) — data em que o recado some da Home automaticamente e depois é apagado do arquivo. O campo começa <strong>em branco</strong>; se ficar vazio ou com data inválida, o sistema avisa e não deixa salvar. Recados <strong>fixos</strong> (📌) não mostram esse campo — expiram sozinhos em 10 anos.</li>
              <li><strong>📌 Fixar na lateral direita</strong> (opcional) — quando marcado, o recado aparece numa coluna lateral fixa em todas as páginas do portal, não só na Home. Limite de <strong>5 recados fixos ativos</strong>.</li>
            </ul>
          </li>
          <li>Clique em <strong>Confirmar</strong> para adicionar à lista.</li>
          <li>Clique em <strong>Salvar alterações</strong> (botão azul no topo).</li>
        </ol>

        <h4>Recados fixos na lateral</h4>
        <p>Use o botão <strong>📌</strong> da tabela (ou o checkbox no formulário) para fixar um recado. Recados fixos:</p>
        <ul>
          <li>Aparecem numa coluna lateral à direita, visível em todas as abas do portal.</li>
          <li>São limitados a <strong>5 ativos</strong>. O 6º só entra quando você desafixa outro.</li>
          <li>Recados fixos que expiram somem da lateral automaticamente — mas não liberam slot até serem removidos do arquivo (ou desafixados manualmente).</li>
          <li>A ordem na lateral segue a <strong>prioridade</strong> (mesma da Home).</li>
        </ul>

        <h4>Pelo menos texto ou imagem</h4>
        <p>O botão Confirmar fica desabilitado até você preencher ao menos um dos dois. Título sozinho não é suficiente.</p>

        <h4>Ordenar os recados</h4>
        <p>Na tabela, a coluna <strong>#</strong> mostra a prioridade de cada recado. Para reorganizar:</p>
        <ul>
          <li>Setas <strong>▲</strong> e <strong>▼</strong> ao lado do número — sobem ou descem um recado uma posição de cada vez e já renumeram automaticamente.</li>
          <li>Botão <strong>Renumerar prioridades (1, 2, 3…)</strong> — reorganiza sequencialmente sem buracos. Útil quando você apagou vários recados e os números ficaram salteados (ex: 1, 3, 7).</li>
          <li>Ao editar, você pode digitar qualquer número no campo Prioridade (ex: colocar o novo recado em 5 para ele ficar no meio).</li>
        </ul>
        <p>Na Home, os recados aparecem da esquerda pra direita, de cima pra baixo, seguindo a prioridade crescente. Limite: 30 recados por Home.</p>

        <h4>Expiração automática (obrigatória)</h4>
        <p>Todo recado precisa de uma data em <strong>Expira em</strong>: ele some da Home assim que a data passa e, na próxima vez que alguém abrir o portal, é removido do arquivo. Se você deixar em branco ou digitar uma data inválida, aparece a mensagem <em>"Data de expiração inválida ou não preenchida"</em> e o recado não é salvo. <strong>Recados fixos</strong> (📌) expiram automaticamente em 10 anos, sem precisar preencher. Recados antigos que ainda não tinham data receberam 1 ano de validade (10 anos se fixos) automaticamente.</p>

        <h4>Editar ou apagar</h4>
        <p>Na tabela de recados, use <strong>Editar</strong> para alterar conteúdo e <strong>Remover</strong> para apagar em definitivo. Sempre <strong>Salvar alterações</strong> depois.</p>
      </section>

      <section id="manual-reacoes" className="manual-section">
        <h3>Reações e sino de notificações</h3>
        <p>Esses dois recursos aparecem na <strong>visão do usuário</strong> e funcionam sozinhos — não precisam de nenhuma configuração no admin.</p>

        <h4>Reações (👍 / 👎)</h4>
        <ul>
          <li>Cada recado na Home tem botões de <strong>positivo</strong> e <strong>negativo</strong> no rodapé do card.</li>
          <li>Cada pessoa dá <strong>um voto</strong>; clicar de novo remove o voto; clicar no outro botão troca.</li>
          <li>As contagens são somadas e ficam visíveis para todos — útil pra sentir a reação a um comunicado.</li>
          <li>Passe o mouse sobre a contagem para ver a <strong>lista de quem reagiu</strong> (positivo ou negativo).</li>
        </ul>

        <h4>Sino de notificações</h4>
        <ul>
          <li>No canto superior direito aparece um <strong>sino</strong> com o número de recados <strong>não lidos</strong> daquele usuário.</li>
          <li>Abrir o sino <strong>não</strong> marca nada como lido — ele apenas lista todos os recados, com os não lidos destacados.</li>
          <li>Um recado só é marcado como lido quando o usuário <strong>clica nele</strong> na lista (e é levado direto pra Home). Aí o contador diminui.</li>
          <li>É individual: o que uma pessoa leu não afeta o contador das outras.</li>
        </ul>
      </section>

      <section id="manual-abas" className="manual-section">
        <h3>Gerenciar abas</h3>
        <p>Abas são as seções da barra lateral (ex: "Documentação", "Sistemas", "RH"). Cada aba pode ser pública ou restrita.</p>

        <h4>Criar uma aba</h4>
        <ol>
          <li>Vá em <strong>Abas</strong> → <strong>+ Nova aba</strong>.</li>
          <li>Informe o <strong>Nome da aba</strong> (aparece na sidebar).</li>
          <li>Escolha a <strong>Visibilidade</strong>:
            <ul>
              <li><strong>Todos os usuários</strong> — qualquer pessoa que logar no portal vê.</li>
              <li><strong>Somente líderes</strong> — apenas perfis admin e líder enxergam a aba.</li>
            </ul>
          </li>
          <li>Escolha o <strong>Ícone</strong> (segue a paleta semântica): pasta para manuais, gráfico para métricas, prancheta para formulários, formatura para cursos, cadeado para conteúdo restrito/segurança, pessoas para assuntos gerais. Sem seleção, usa pasta como padrão.</li>
          <li><strong>Confirmar</strong> → <strong>Salvar alterações</strong>.</li>
        </ol>

        <h4>Renomear, mudar acesso ou trocar o ícone</h4>
        <p>Clique em <strong>Editar</strong> na linha da aba. Você pode renomear, mover entre pública/restrita, ou trocar o ícone. Os links dentro dela vão junto.</p>

        <h4>Remover uma aba</h4>
        <p>Clique em <strong>Remover</strong>. Se a aba tiver links, o sistema pede confirmação — ao apagar, todos os links daquela aba vão junto.</p>

        <div className="manual-callout">
          <strong>Atenção:</strong> a aba <em>Home</em> é fixa do sistema e não aparece nessa lista.
        </div>
      </section>

      <section id="manual-links" className="manual-section">
        <h3>Gerenciar links</h3>
        <p>Links são os cards clicáveis que aparecem dentro de cada aba. Cada card tem ícone colorido, título, descrição e URL.</p>

        <h4>Criar um link</h4>
        <ol>
          <li>Vá em <strong>Links</strong> → <strong>+ Novo link</strong>.</li>
          <li>Preencha:
            <ul>
              <li><strong>Nome</strong> — título do card (ex: "PowerTickets").</li>
              <li><strong>Descrição</strong> — linha curta que aparece abaixo do título (ex: "Demandas, horas e horas extras").</li>
              <li><strong>URL</strong> — endereço completo começando por <code>https://</code>.</li>
              <li><strong>Aba de destino</strong> — onde esse link vai aparecer no portal. A lista mostra só abas que já existem; crie a aba antes se for uma nova seção.</li>
              <li><strong>Sub-aba</strong> (opcional) — categoria dentro da aba. As opções saem da lista cadastrada em <strong>Configurações → Categorias</strong> (você mesmo cria e ordena). Dentro da aba, o usuário vê pílulas que filtram os cards por essa categoria. Deixe em "Sem categoria" se o link for de uso geral.</li>
              <li><strong>Cor do ícone</strong> — visual do quadradinho à esquerda do card. Escolha a cor que faz sentido para o tipo de ferramenta.</li>
            </ul>
          </li>
          <li><strong>Confirmar</strong> → <strong>Salvar alterações</strong>.</li>
        </ol>

        <h4>Padrão semântico de cores</h4>
        <p>Cada cor carrega um significado fixo para que o usuário identifique o tipo do link rapidamente:</p>
        <ul>
          <li><strong>Cinza</strong> (pasta) — manuais, normas e procedimentos.</li>
          <li><strong>Verde</strong> (gráfico de crescimento) — métricas, progresso e análise de dados.</li>
          <li><strong>Âmbar</strong> (prancheta) — listas de afazeres e formulários.</li>
          <li><strong>Roxo</strong> (chapéu de formatura) — cursos e treinamentos.</li>
          <li><strong>Azul</strong> (cadeado) — privacidade e segurança.</li>
          <li><strong>Coral</strong> (pessoas) — assuntos gerais da empresa, comuns a todas as áreas.</li>
        </ul>
        <p>Use o significado como guia — o usuário aprende a associar cor + ícone ao tipo de conteúdo.</p>

        <h4>Mover um link entre abas</h4>
        <p>Clique em <strong>Editar</strong> no link e troque o valor de <strong>Aba de destino</strong>. O link sai da aba antiga e aparece na nova depois de salvar.</p>
      </section>

      <section id="manual-usuarios" className="manual-section">
        <h3>Permissões de usuários</h3>
        <p>A maioria dos usuários é reconhecida automaticamente pelo cargo no Azure AD — <strong>Head</strong>, <strong>Team Leader</strong>, <strong>Tech Leader</strong> e <strong>P&amp;C</strong> viram administradores sem você precisar cadastrar nada.</p>

        <p>Use a aba <strong>Usuários</strong> quando quiser uma exceção (ex: promover alguém a admin mesmo sem cargo, ou rebaixar alguém).</p>

        <h4>Cadastrar ou alterar um usuário</h4>
        <ol>
          <li>Vá em <strong>Usuários</strong> → <strong>+ Novo usuário</strong> (ou <strong>Editar</strong> numa linha existente).</li>
          <li>Digite o <strong>e-mail completo</strong> do colaborador (<code>nome.sobrenome@powertuning.com.br</code>).</li>
          <li>Escolha o <strong>Perfil</strong>:
            <ul>
              <li><strong>Administrador</strong> — enxerga tudo, acessa esta área e edita o portal.</li>
              <li><strong>Líder</strong> — enxerga abas públicas e restritas, mas não acessa a administração.</li>
              <li><strong>Usuário</strong> — enxerga apenas abas públicas.</li>
            </ul>
          </li>
          <li><strong>Confirmar</strong> → <strong>Salvar alterações</strong>.</li>
        </ol>

        <p>Os cadastros aqui <strong>têm prioridade</strong> sobre o reconhecimento automático pelo cargo. Se alguém foi detectado como admin pelo AD e você o rebaixa aqui, ele vira usuário normal.</p>

        <p>Usuários não cadastrados entram como <strong>Usuário</strong> por padrão, a menos que o cargo no AD os promova.</p>
      </section>

      <section id="manual-config" className="manual-section">
        <h3>Logo e categorias</h3>

        <h4>Trocar a logo</h4>
        <ol>
          <li>Vá em <strong>Configurações</strong>.</li>
          <li>Clique no retângulo pontilhado ou arraste a imagem — PNG com fundo transparente funciona melhor.</li>
          <li>A pré-visualização aparece na hora; se não gostou, clique no <strong>×</strong> para remover.</li>
          <li><strong>Salvar alterações</strong>. A logo nova aparece na sidebar na próxima carga da página.</li>
        </ol>
        <p>Para voltar à logo padrão do sistema, remova a imagem com o <strong>×</strong> e salve.</p>

        <h4>Categorias (sub-abas) de recados e links</h4>
        <p>Ainda em <strong>Configurações</strong>, abaixo da logo, ficam as <strong>Categorias</strong>: são as opções que aparecem no campo "Sub-aba" ao criar recados e links, e que viram as pílulas de filtro na Home e dentro das abas. A mesma lista serve para recados e links.</p>
        <ul>
          <li><strong>Adicionar</strong> — digite o nome no campo e clique em <strong>+ Adicionar</strong> (ou tecle Enter).</li>
          <li><strong>Ordenar</strong> — as setas <strong>↑ ↓</strong> mudam a ordem, que é a mesma ordem das pílulas no portal.</li>
          <li><strong>Remover</strong> — tira a categoria da lista. Recados e links que já a usavam <strong>não são apagados</strong>: só deixam de ter a pílula de filtro (continuam aparecendo em "Todos").</li>
          <li>Depois de mexer, clique em <strong>Salvar alterações</strong>.</li>
        </ul>
      </section>

      <section id="manual-logs" className="manual-section">
        <h3>Logs e auditoria</h3>
        <p>A aba <strong>Logs</strong> reúne dois registros:</p>
        <ul>
          <li><strong>Últimos acessos</strong> — lista ordenada alfabeticamente, com <strong>nome, e-mail, total de acessos e data do último login</strong> de cada usuário. O contador é incrementado a cada autenticação MSAL.</li>
          <li><strong>Alterações (últimos 15 dias)</strong> — histórico de quem fez o quê na administração: criação, edição e remoção de recados, links, abas, usuários e configurações. Registros com mais de 15 dias são apagados automaticamente.</li>
        </ul>
        <p>Os dois registros têm um campo de busca no canto direito para filtrar por nome, e-mail, descrição ou tipo de ação.</p>
        <div className="manual-callout">
          <strong>Importante:</strong> o log de alterações só é persistido quando você clica em <strong>Salvar alterações</strong>. Se abrir o modal de um recado, editar algo e fechar sem salvar, não é registrado nada.
        </div>
        <h4>Logout automático após 8 horas</h4>
        <p>A sessão expira automaticamente 8h depois do primeiro login. O usuário recebe um alerta e é redirecionado para a tela de login. Recarregar a página não reinicia o cronômetro — fechar a aba sim.</p>
      </section>

      <section id="manual-dicas" className="manual-section">
        <h3>Dicas e solução de problemas</h3>

        <h4>"Minha alteração não apareceu"</h4>
        <ul>
          <li>Confira se clicou em <strong>Salvar alterações</strong> (o botão fica verde com "Salvo!" por alguns segundos quando dá certo).</li>
          <li>Dê um <strong>Ctrl+F5</strong> para forçar o navegador a recarregar.</li>
          <li>Abra uma janela anônima para descartar cache e sessão antiga.</li>
        </ul>

        <h4>"Imagem do recado não carrega"</h4>
        <ul>
          <li>Tente fazer o upload de novo — às vezes a conexão falhou no meio do caminho.</li>
          <li>Se o arquivo for maior que ~5 MB, reduza antes (qualquer ferramenta de compressão serve).</li>
          <li>Formatos suportados: PNG, JPG, GIF, WebP.</li>
        </ul>

        <h4>"Quero desfazer uma alteração"</h4>
        <p>Enquanto você <strong>não clicou em Salvar</strong>, basta fechar e reabrir a Administração — tudo volta ao último estado salvo. Depois de salvar, é preciso editar manualmente para reverter.</p>

        <h4>"A aba sumiu para alguns usuários"</h4>
        <p>Provavelmente ela foi marcada como <strong>restrita</strong>. Líderes e administradores enxergam; usuários normais não. Edite a aba e mude a visibilidade se precisar.</p>

        <h4>Quando pedir ajuda técnica</h4>
        <p>Se ao tentar salvar aparecer um erro vermelho persistente, ou se o portal inteiro não carregar, entre em contato com a equipe técnica com um print da tela e, se possível, do console do navegador (<kbd>F12</kbd> → aba Console).</p>
      </section>
    </div>
  );
}

function sortByTitle(a, b) {
  // Ordena alfabeticamente pelo nome do link, ignorando maiúsculas/acentos (pt-BR)
  return (a.title || "").localeCompare(b.title || "", "pt-BR", { sensitivity: "base" });
}

function comparePriority(a, b) {
  // Recados com prioridade definida (>0) vêm antes. Empate: mais recente primeiro.
  const pa = a.priority && a.priority > 0 ? a.priority : 9999;
  const pb = b.priority && b.priority > 0 ? b.priority : 9999;
  if (pa !== pb) return pa - pb;
  return (b.createdAt || "").localeCompare(a.createdAt || "");
}

function toLocalInput(date) {
  // Converte Date → string "YYYY-MM-DDTHH:mm" no fuso local, aceito por <input type="datetime-local">
  const pad = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Retorna uma nova Date com N anos somados (não muta a original).
function plusYears(date, years) {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

function matchesView(target, view) {
  // A view "links" e "tabs" compartilham o mesmo blob (links.json),
  // então qualquer save em uma delas persiste as duas.
  if (view === "links" || view === "tabs") return target === "link" || target === "tab";
  if (view === "users") return target === "user";
  if (view === "announcements") return target === "announcement";
  if (view === "settings") return target === "settings";
  return false;
}

function clone(obj) { return JSON.parse(JSON.stringify(obj)); }
function slugify(str) { return str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""); }
function roleLabel(role) {
  const r = ROLE_OPTIONS.find(o => o.value === role);
  return r ? r.label : role;
}
function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
